import { Button, Form, Input, Radio, Spin } from "antd";
import { useForm } from "antd/lib/form/Form";
import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";

import { BreadCrumb } from "../../../components/main/LayoutComponents";
import { Locale } from "../../../utils";
import {
  handleMutationError,
  showNotificationWithIcon,
} from "../../../utils/helpers/helpers";
import { create, getOne, update } from "../../../utils/http/dataProvider";
import { getUserInfo } from "../../../utils/redux/reducers/User";
import "./AddOrEditStaff.scss";

const i18n = Locale.getInstance();

const AddOrEditStaff = () => {
  const user = useSelector((state) => getUserInfo(state));
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();
  const [staffForm] = useForm();
  const history = useHistory();
  const location = useLocation();
  const id = location?.state?.id;

  const { data, isLoading } = useQuery(
    ["merchant-employee-detail", user.token, id],
    () => getOne("merchant-employee", { id, token: user.token }),
    {
      enabled: !!id,
    }
  );

  useEffect(() => {
    if (data && id === data?.id) {
      staffForm.setFieldsValue({
        employee_name: data.employee_name,
        mobile: data?.user?.mobile || "",
        status: Number(data.status) === 0 ? 0 : 1,
        password: "",
      });
    }
    if (!id) {
      staffForm.setFieldsValue({
        employee_name: "",
        mobile: "",
        status: 1,
        password: "",
      });
    }
  }, [data, id, staffForm]);

  const addMutation = useMutation(create, {
    onSuccess: () => {
      queryClient.invalidateQueries("merchant-employees");
      showNotificationWithIcon(i18n.t("media.saveFinished"), "success");
      setLoading(false);
      history.goBack();
    },
    onError: (error) => {
      handleMutationError(error);
      setLoading(false);
    },
  });

  const updateMutation = useMutation(update, {
    onSuccess: () => {
      queryClient.invalidateQueries("merchant-employees");
      showNotificationWithIcon(i18n.t("media.saveFinished"), "success");
      setLoading(false);
      history.goBack();
    },
    onError: (error) => {
      handleMutationError(error);
      setLoading(false);
    },
  });

  const onFinish = (values) => {
    setLoading(true);
    const payload = {
      employee_name: values.employee_name,
      status: values.status,
    };
    if (values.password) {
      payload.password = values.password;
    }
    if (!id) {
      payload.mobile = values.mobile;
    }

    const params = {
      data: payload,
      token: user.token,
    };

    if (id) {
      updateMutation.mutate({
        resource: "merchant-employee",
        params: { ...params, id },
      });
    } else {
      addMutation.mutate({ resource: "merchant-employee", params });
    }
  };

  const onFinishFailed = (errorInfo) => {
    setLoading(false);
    showNotificationWithIcon(errorInfo);
  };

  const breadCrumbData = [
    {
      href: "/main/setting/staff",
      title: i18n.t("merchantStaff.title"),
    },
    {
      title: id ? i18n.t("merchantStaff.edit") : i18n.t("merchantStaff.add"),
    },
  ];

  if (isLoading) {
    return <Spin spinning={isLoading} />;
  }

  return (
    <>
      <BreadCrumb data={breadCrumbData} />
      <Spin spinning={loading}>
        <Form
          form={staffForm}
          className="staff-form"
          name="merchant-staff"
          labelCol={{ span: 4 }}
          wrapperCol={{ span: 12 }}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          autoComplete="off"
        >
          <Form.Item
            name="employee_name"
            label={i18n.t("merchantStaff.name")}
            rules={[
              {
                required: true,
                message: i18n.t("productEdit.standardRequired"),
              },
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="mobile"
            label={i18n.t("merchantStaff.mobile")}
            rules={
              id
                ? []
                : [
                    {
                      required: true,
                      message: i18n.t("productEdit.standardRequired"),
                    },
                  ]
            }
          >
            <Input disabled={!!id} />
          </Form.Item>

          <Form.Item
            name="password"
            label={i18n.t("login.password")}
            extra={
              id
                ? i18n.t("merchantStaff.passwordHintEdit")
                : i18n.t("merchantStaff.passwordHintCreate")
            }
          >
            <Input.Password />
          </Form.Item>

          <Form.Item
            name="status"
            label={i18n.t("merchantStaff.status")}
            initialValue={1}
          >
            <Radio.Group>
              <Radio value={1}>{i18n.t("merchantStaff.statusActive")}</Radio>
              <Radio value={0}>{i18n.t("merchantStaff.statusInactive")}</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item className="submit" wrapperCol={{ offset: 4, span: 12 }}>
            <Button type="primary" htmlType="submit">
              {i18n.t("productEdit.saveAndBack")}
            </Button>
          </Form.Item>
        </Form>
      </Spin>
    </>
  );
};

export default AddOrEditStaff;
